import * as Y from 'yjs';
import type { Migration } from './types.js';

const LEGACY_ID = 'unsorted';
const MAIN_ID = 'main';

/**
 * v2 -> v3: rename the default collection id from `unsorted` to `main`.
 *
 * The collection is still the protected default home for new and orphaned recipes; only
 * its id and default display name change. Existing households that already renamed it
 * keep their custom name.
 *
 * Idempotent and deterministic: a document with no `unsorted` entry is a no-op, and two
 * replicas performing the same rewrite produce identical maps so Yjs merges cleanly.
 */
export const unsortedToMain: Migration = {
  version: 3,
  name: 'unsorted-to-main',
  migrate(doc: Y.Doc): void {
    const collections = doc.getMap('collections');
    const legacy = collections.get(LEGACY_ID);

    if (legacy instanceof Y.Map) {
      if (!collections.has(MAIN_ID)) {
        const next = new Y.Map<unknown>();
        collections.set(MAIN_ID, next);
        for (const [key, value] of legacy.entries()) {
          next.set(key, value);
        }
        if (next.get('name') === 'Unsorted') {
          next.set('name', 'Main');
        }
        if (next.get('description') === 'Recipes that are not in any other collection yet.') {
          next.set('description', 'Your default collection. New recipes are saved here.');
        }
      }
      collections.delete(LEGACY_ID);
    }

    const recipes = doc.getMap('recipes');
    for (const [, node] of recipes.entries()) {
      if (!(node instanceof Y.Map)) continue;
      const membership = node.get('collections');
      if (!(membership instanceof Y.Map)) continue;
      if (!membership.has(LEGACY_ID)) continue;

      const order = membership.get(LEGACY_ID);
      if (typeof order === 'string' && !membership.has(MAIN_ID)) {
        membership.set(MAIN_ID, order);
      }
      membership.delete(LEGACY_ID);
    }
  },
};
